import React, { useState, useRef } from "react";
import "./fileUploader.css";
import UploadIcon from "./assets/uploadIcon.png";
import Spinner from "../../spinner";

/**
 * Props:
 * - accept: string of accepted file types (e.g. "image/*" or ".pdf" or "image/*,application/pdf")
 * - multiple: boolean (true = allow multiple files, false = only one file)
 * - onFilesSelected: function(files) => void  // callback to pass files to the parent
 */
const FileUploader = ({
  accept = "*/*",
  multiple = false,
  onFilesSelected = () => {},
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const inputRef = useRef(null);

  // Trigger the hidden input when user clicks
  const handleClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  // Handle files from input or drop
  const handleFiles = async (files) => {
    setLoading(true);
    // Convert FileList to an array if needed
    const filesArray = Array.from(files);
    // Pass the selected file(s) to the parent
    await onFilesSelected(filesArray);
    setUploadedFiles(filesArray);
    setLoading(false);
  };

  // Input onChange
  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  // Drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  return (
    <div
      className={`file-uploader-container ${dragActive ? "drag-active" : ""}`}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <div className="file-uploader-content">
        <img src={UploadIcon} alt="Upload Icon" className="upload-icon" />
        <p className="upload-box-text">
          Drag and drop or <span className="select-file-text">select file</span>
        </p>
      </div>

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        style={{ display: "none" }}
      />
      {/* Loading spinner */}
      {loading && (
        <div className="file-uploader-spinner-overlay">
          <Spinner />
        </div>
      )}
      {/* Uploaded file names */}
      {!loading && uploadedFiles.length > 0 && (
        <div className="file-uploader-file-list">
          {uploadedFiles.map((file, idx) => (
            <div key={idx} className="file-uploader-file-name">
              {file.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUploader;
